import { useState } from 'react';
import { Panel, Flex, Button, Typography, Switch } from '@maxhub/max-ui';
import { useAuthStore } from '../store/auth.store.ts';
import { register } from '../api/auth.ts';
import { createPlatformBridge } from '../platform/index.ts';
import { ConsentPage } from './ConsentPage.tsx';

const bridge = createPlatformBridge();

function maskPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length >= 10) {
    return `+7 *** ***-**-${digits.slice(-2)}`;
  }
  return phone;
}

export function RegisterPage() {
  const { setAuth } = useAuthStore();
  const [phone, setPhone] = useState<string | null>(null);
  const [phoneRequesting, setPhoneRequesting] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [addressChecked, setAddressChecked] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const platformUser = bridge.getUser();
  const canSubmit = !!phone && consentChecked && addressChecked;

  if (showConsent) {
    return <ConsentPage onBack={() => setShowConsent(false)} />;
  }

  async function handleRequestPhone() {
    setPhoneRequesting(true);
    try {
      const result = await bridge.requestPhone();
      setPhone(result);
    } catch {
      // пользователь отказался
    } finally {
      setPhoneRequesting(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || !phone) return;
    setSubmitting(true);
    setError(null);

    try {
      const platform = bridge.getPlatform() === 'mock' ? 'max' : bridge.getPlatform();
      if (platform === 'mock') {
        setError('Регистрация доступна только в приложении MAX или Telegram');
        return;
      }

      const result = await register({
        platform,
        initData: bridge.getInitData(),
        phone,
      });

      if (result.requiresPhone) {
        setError('Не удалось подтвердить номер телефона. Попробуйте ещё раз.');
        return;
      }

      bridge.impactOccurred('light');
      setAuth(result.token, result.userId, result.isApproved, result.needsOnboarding, result.role);
    } catch {
      setError('Ошибка регистрации. Попробуйте ещё раз.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Panel
      style={{
        minHeight: '100vh',
        padding: '2rem 1rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Flex direction="column" align="center" gap={4} style={{ marginBottom: '2rem' }}>
        <div style={{ fontSize: '3rem' }}>💧</div>
        <Typography.Headline variant="large-strong" style={{ display: 'block' }}>
          «Водомерка»
        </Typography.Headline>
        <Typography.Label style={{ display: 'block', opacity: 0.6 }}>
          ТСН «Рокада»
        </Typography.Label>
      </Flex>

      <Panel
        mode="secondary"
        style={{ width: '100%', maxWidth: 400, borderRadius: 12, padding: '1.5rem' }}
      >
        <form onSubmit={handleSubmit}>
          <Typography.Label style={{ display: 'block', marginBottom: '1.5rem' }}>
            Добро пожаловать, <strong>{platformUser.firstName || 'пользователь'}</strong>!
          </Typography.Label>

          <div style={{ marginBottom: '1.5rem' }}>
            {phone ? (
              <Flex align="center" gap={8}>
                <Typography.Label style={{ color: '#2e7d32', fontWeight: 600 }}>
                  ✓ {maskPhone(phone)}
                </Typography.Label>
                <Button mode="link" size="small" onClick={() => setPhone(null)}>
                  Изменить
                </Button>
              </Flex>
            ) : (
              <Button
                mode="primary"
                stretched
                loading={phoneRequesting}
                disabled={phoneRequesting}
                onClick={handleRequestPhone}
              >
                📱 Поделиться номером
              </Button>
            )}
          </div>

          <Flex align="flex-start" gap={8} style={{ marginBottom: '0.75rem' }}>
            <Switch
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
            />
            <Typography.Label style={{ display: 'block' }}>
              Я даю согласие на{' '}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setShowConsent(true);
                }}
              >
                обработку персональных данных
              </a>
            </Typography.Label>
          </Flex>

          <Flex align="flex-start" gap={8} style={{ marginBottom: '1.5rem' }}>
            <Switch
              checked={addressChecked}
              onChange={(e) => setAddressChecked(e.target.checked)}
            />
            <Typography.Label style={{ display: 'block' }}>
              Я подтверждаю, что проживаю по адресу: Долгопрудная аллея, домовладение 1
            </Typography.Label>
          </Flex>

          {error && (
            <Typography.Label style={{ display: 'block', color: '#dc2626', marginBottom: '1rem' }}>
              {error}
            </Typography.Label>
          )}

          <Button
            type="submit"
            mode="primary"
            size="large"
            stretched
            loading={submitting}
            disabled={!canSubmit || submitting}
          >
            Зарегистрироваться
          </Button>
        </form>
      </Panel>
    </Panel>
  );
}
